import { RouteConfig } from 'vue-router';
import { loanDetailsResolver } from '@/loan/routing/resolvers/loan-details';
import { loanStatementsResolver } from '@/loan/routing/resolvers/loan-statements';
import { loanHelpCenterFAQResolver } from '@/loan/routing/resolvers/loan-help-center-faq';
import { loanHelpCenterVersionsResolver } from '@/loan/routing/resolvers/loan-help-center-versions';
import { loanTransactionsResolver } from '@/loan/routing/resolvers/loan-transactions';
import { loanLinkedAccountsResolver } from '@/loan/routing/resolvers/loan-linked-accounts';
import { loanRecurringPaymentResolver } from '@/loan/routing/resolvers/loan-recurring-payment';
import LoanSummary from '@/loan/pages/LoanSummary/LoanSummary.vue';
import LoanMonthly from '@/loan/pages/LoanMonthly.vue';
import LoanDetails from '@/loan/pages/LoanDetails.vue';
import LoanOneTime from '@/loan/pages/LoanOneTime.vue';
import LoanOneTimeConfirm from '@/loan/pages/LoanOneTimeConfirm.vue';
import LoanLayout from '@/loan/components/LoanLayout.vue';
import LoanTakeBack from '@/loan/pages/LoanTakeBack.vue';
import LoanPlugin from '@/loan/pages/LoanPlugin.vue';
import LoanTakeBackConfirm from '@/loan/pages/LoanTakeBackConfirm.vue';
import LoanTransactions from '@/loan/pages/LoanTransactions.vue';
import LoanStatements from '@/loan/pages/LoanStatements.vue';
import LoanAutopay from '@/loan/pages/LoanAutopay.vue';
import HelpCenterFAQ from '@/help-center/pages/HelpCenterFAQ.vue';
import HelpCenterVersions from '@/help-center/pages/HelpCenterVersions.vue';
import { loanAutopayRoutePropHandler } from '@/loan/routing/prop-handlers/loan-autopay';
import { loanMonthlyRoutePropHandler } from '@/loan/routing/prop-handlers/loan-monthly';
import { loanProjectionResolver } from '@/loan/routing/resolvers/loan-projection';
import { LoanProjectionType } from '@/loan/enums/loan-projection-type';
import { sessionResolver } from '@/core/routing/resolvers/session';
import { loanCoreResolver } from '@/loan/routing/resolvers/loan-core';
import { loanRouteResolver } from '@/loan/routing/resolvers/loan-route-resolver';
import { moduleRootRouteResolver } from '@/core/routing/resolvers/module-root-route-resolver';

export const LoanRoutes: RouteConfig[] = [{
	path: '/loan/:loanId/plugin',
	name: 'LoanPlugin',
	component: LoanPlugin,
	meta: {
		group: 'Overview',
		groupTitle: 'Your Loan',
		pageTitle: 'Take-Back Your Money',
	},
	beforeEnter: loanRouteResolver(
		loanDetailsResolver,
		loanProjectionResolver(LoanProjectionType.TAKE_BACK),
	),
},
{
	path: '/loan/:loanId',
	component: LoanLayout,
	beforeEnter: moduleRootRouteResolver(loanCoreResolver),
	children: [
		{
			path: '',
			redirect: {
				name: 'LoanSummary',
			},
			meta: {
				group: 'Overview',
			},
		},
		{
			path: 'summary',
			name: 'LoanSummary',
			component: LoanSummary,
			meta: {
				group: 'Overview',
				groupTitle: 'Your Loan',
				pageTitle: 'Summary',
			},
			beforeEnter: loanRouteResolver(
				loanDetailsResolver,
				loanTransactionsResolver,
				loanRecurringPaymentResolver,
				loanProjectionResolver(),
			),
		},
		{
			path: 'monthly',
			name: 'LoanMonthly',
			component: LoanMonthly,
			meta: {
				group: 'Overview',
				groupTitle: 'Your Loan',
				pageTitle: 'Monthly Payment',
			},
			beforeEnter: loanRouteResolver(
				loanDetailsResolver,
				loanProjectionResolver(),
				loanTransactionsResolver,
			),
			props: loanMonthlyRoutePropHandler,
		},
		{
			path: 'autopay',
			name: 'LoanAutopay',
			component: LoanAutopay,
			meta: {
				group: 'Overview',
				groupTitle: 'Your Loan',
				pageTitle: 'Autopay',
			},
			beforeEnter: loanRouteResolver(
				sessionResolver,
				loanDetailsResolver,
				loanLinkedAccountsResolver,
				loanRecurringPaymentResolver,
			),
			props: loanAutopayRoutePropHandler,
		},
		{
			path: 'one-time',
			name: 'LoanOneTime',
			component: LoanOneTime,
			meta: {
				group: 'Overview',
				groupTitle: 'Your Loan',
				pageTitle: 'One Time Payment',
			},
			beforeEnter: loanRouteResolver(
				loanDetailsResolver,
				loanTransactionsResolver,
			),
		},
		{
			path: 'one-time/confirm',
			name: 'LoanOneTimeConfirm',
			component: LoanOneTimeConfirm,
			meta: {
				group: 'Overview',
				groupTitle: 'Your Loan',
				pageTitle: 'One Time Payment',
			},
			beforeEnter: loanRouteResolver(loanLinkedAccountsResolver),
			props: (route) => ({
				amount: Number(route.query.amount ?? 0),
			}),
		},
		{
			path: 'take-back',
			name: 'LoanTakeBack',
			component: LoanTakeBack,
			meta: {
				group: 'Overview',
				groupTitle: 'Your Loan',
				pageTitle: 'Take-Back Your Money',
			},
			beforeEnter: loanRouteResolver(
				loanDetailsResolver,
				loanProjectionResolver(LoanProjectionType.TAKE_BACK),
			),
		},
		{
			path: 'take-back/confirm',
			name: 'LoanTakeBackConfirm',
			component: LoanTakeBackConfirm,
			meta: {
				group: 'Overview',
				groupTitle: 'Your Loan',
				pageTitle: 'Take-Back Your Money',
			},
			beforeEnter: loanRouteResolver(loanLinkedAccountsResolver),
			props: (route) => ({
				amount: Number(route.query.amount ?? 0),
			}),
		},
		{
			path: 'details',
			name: 'LoanDetails',
			component: LoanDetails,
			meta: {
				group: 'Details',
				groupTitle: 'Loan Details',
			},
			beforeEnter: loanRouteResolver(
				loanDetailsResolver,
				loanLinkedAccountsResolver,
			),
		},
		{
			path: 'transactions',
			name: 'LoanTransactions',
			component: LoanTransactions,
			meta: {
				group: 'Activity',
				groupTitle: 'Activity',
				pageTitle: 'Transactions',
			},
			beforeEnter: loanRouteResolver(loanTransactionsResolver),
		},
		{
			path: 'statements',
			name: 'LoanStatements',
			component: LoanStatements,
			meta: {
				group: 'Activity',
				groupTitle: 'Activity',
				pageTitle: 'Statements',
			},
			beforeEnter: loanRouteResolver(loanStatementsResolver),
		},
		{
			path: 'help-center',
			name: 'LoanHelpCenterFAQ',
			component: HelpCenterFAQ,
			meta: {
				group: 'HelpCenter',
				groupTitle: 'Help Center',
				pageTitle: 'FAQ',
			},
			beforeEnter: loanRouteResolver(loanHelpCenterFAQResolver),
		},
		{
			path: 'help-center/whats-new',
			name: 'LoanHelpCenterWhatsNew',
			component: HelpCenterVersions,
			meta: {
				group: 'HelpCenter',
				groupTitle: 'Help Center',
				pageTitle: 'What\'s New',
			},
			beforeEnter: loanRouteResolver(loanHelpCenterVersionsResolver),
		},
	],
},
];
